import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
	Appointment,
	AppointmentResponse,
} from 'src/shared/models/appointment.model';
import { BaseResponse } from 'src/shared/models/base-response.model';
import {
	Dependent,
	DependentResponse,
} from 'src/shared/models/dependent.model';
import {
	EmergencyInfo,
	EmergencyInfoResponse,
} from 'src/shared/models/emergency-info.model';
import { Housing, HousingResponse } from 'src/shared/models/housing.model';
import { Pet, PetResponse } from 'src/shared/models/pet.model';
import {
	Residency,
	ResidencyResponse,
} from 'src/shared/models/residency.model';
import {
	UserPassport,
	UserPassportResponse,
} from 'src/shared/models/user-passport.model';
import {
	UserPhoneInfo,
	UserPhoneInfoResponse,
} from 'src/shared/models/user-phone-info.model';
import { UserQatarInfo } from 'src/shared/models/user-qatar-info.model';
import { User, UserResponse } from 'src/shared/models/user.model';
import { BackendService } from './backend.service';

const API_URL = `${environment.apiUrl}/web/my`;

export const USER_URL = `${API_URL}/user`;
export const USER_RESPONSE = 'Fetched User';

export const CONFIRM_URL = `${API_URL}/confirm`;
export const CONFIRM_RESPONSE = 'User Confirmed';

export const APPOINTMENTS_URL = `${API_URL}/appointments`;
export const APPOINTMENTS_RESPONSE = 'Fetched Appointments';

export const RESIDENCY_URL = `${API_URL}/residency`;
export const RESIDENCY_RESPONSE = 'Fetched Residency';

export const HOUSING_URL = `${API_URL}/housing`;
export const HOUSING_RESPONSE = 'Fetched Housing Info';

export const PETS_URL = `${API_URL}/pets`;
export const PETS_RESPONSE = 'Fetched Pets';

export const SAVE_QATAR_INFO_URL = `${API_URL}/saveqatarinfo`;
export const SAVE_QATAR_INFO_RESPONSE = 'Saved Qatar Info';

export const EMERGENCY_INFO_URL = `${API_URL}/emergencyinfo`;
export const EMERGENCY_INFO_RESPONSE = 'Fetched Emergency Info';

export const DEPENDENTS_URL = `${API_URL}/dependents`;
export const DEPENDENTS_RESPONSE = 'Fetched Dependents';

export const SAVE_EMERGENCY_INFO_URL = `${API_URL}/saveemergencyinfo`;
export const SAVE_EMERGENCY_INFO_RESPONSE = 'Saved Emergency Info';

export const PHONE_INFO_URL = `${API_URL}/phoneinfo`;
export const PHONE_INFO_RESPONSE = 'Fetched Phone Info';

export const PASSPORTS_URL = `${API_URL}/passports`;
export const PASSPORTS_RESPONSE = 'Fetched Passports';

@Injectable({
	providedIn: 'root',
})
export class MyBackendService {
	private backendService = inject(BackendService);

	/**
	 * My User
	 * ========
	 * API Endpoint: /web/my/user
	 *
	 * Returns the currently logged in User
	 */
	user(): Observable<UserResponse> {
		return this.backendService
			.post(USER_URL, USER_RESPONSE, {})
			.pipe(map((response: UserResponse) => response));
	}

	/**
	 * Confirm My User
	 * ================
	 * API Endpoint: /web/my/confirm
	 *
	 * Confirms the currently logged in User's record, and returns an updated value
	 */
	confirm(): Observable<UserResponse> {
		return this.backendService
			.post(CONFIRM_URL, CONFIRM_RESPONSE, {})
			.pipe(map((response: UserResponse) => response));
	}

	/**
	 * My Appointments
	 * ================
	 * API Endpoint: /web/my/appointments
	 *
	 * Selects the currently logged in User's Appointments
	 */
	appointments(): Observable<Appointment[]> {
		return this.backendService
			.post(APPOINTMENTS_URL, APPOINTMENTS_RESPONSE, {})
			.pipe(
				map((response: AppointmentResponse) =>
					response.appointments.map((json) =>
						Appointment.fromJSON(json)
					)
				)
			);
	}

	/**
	 * My Residency
	 * =============
	 * API Endpoint: /web/my/residency
	 *
	 * Selects the currently logged in User's Residency Information
	 */
	residency(): Observable<Residency> {
		return this.backendService
			.post(RESIDENCY_URL, RESIDENCY_RESPONSE, {})
			.pipe(
				map((response: ResidencyResponse) =>
					Residency.fromJSON(response.residency)
				)
			);
	}

	/**
	 * My Housing Info
	 * ================
	 * API Endpoint: /web/my/housing
	 *
	 * Selects the currently logged in User's Housing Information
	 */
	housing(): Observable<Housing> {
		return this.backendService
			.post(HOUSING_URL, HOUSING_RESPONSE, {})
			.pipe(
				map((response: HousingResponse) =>
					Housing.fromJSON(response.housing)
				)
			);
	}

	/**
	 * My Pets
	 * ========
	 * API Endpoint: /web/my/pets
	 *
	 * Selects the currently logged in User's Pets
	 */
	pets(): Observable<Pet[]> {
		return this.backendService
			.post(PETS_URL, PETS_RESPONSE, {})
			.pipe(
				map((response: PetResponse) =>
					response.pets.map((json) => Pet.fromJSON(json))
				)
			);
	}

	/**
	 * Save My Qatar Info
	 * ===================
	 * API Endpoint: /web/my/saveqatarinfo
	 * JSON Parameters: residency, pets
	 *
	 * Saves the currently logged in User's Qatar part of the form
	 */
	saveQatarInfo(userQatarInfo: UserQatarInfo): Observable<BaseResponse> {
		return this.backendService.post(
			SAVE_QATAR_INFO_URL,
			SAVE_QATAR_INFO_RESPONSE,
			{ residency: userQatarInfo.residency, pets: userQatarInfo.pets }
		);
	}

	/**
	 * My Emergency Info
	 * ==================
	 * API Endpoint: /web/my/emergencyinfo
	 *
	 * Selects the currently logged in User's Emergency Information
	 */
	emergencyInfo(): Observable<EmergencyInfoResponse> {
		return this.backendService
			.post(EMERGENCY_INFO_URL, EMERGENCY_INFO_RESPONSE, {})
			.pipe(map((response: EmergencyInfoResponse) => response));
	}

	/**
	 * My Dependents
	 * ==============
	 * API Endpoint: /web/my/userdependents
	 *
	 * Selects the currently logged in User's Dependents w/ Passport Information
	 */
	dependents(): Observable<Dependent[]> {
		return this.backendService
			.post(DEPENDENTS_URL, DEPENDENTS_RESPONSE, {})
			.pipe(
				map((response: DependentResponse) =>
					response.dependents.map((json) => Dependent.fromJSON(json))
				)
			);
	}

	/**
	 * Save Emergency Info
	 * =========================
	 * API Endpoint: /web/my/saveemergencyinfo
	 * JSON Parameters: firstAidCPR, localContacts, otherLanguages
	 *
	 * Saves the User Emergency Info part of the form and returns an updated user
	 */
	saveEmergencyInfo(emergencyInfo: EmergencyInfo): Observable<User> {
		return this.backendService
			.post(SAVE_EMERGENCY_INFO_URL, SAVE_EMERGENCY_INFO_RESPONSE, {
				firstAidCPR: emergencyInfo.firstAidCPR,
				localContacts: emergencyInfo.localContacts,
				otherLanguages: emergencyInfo.otherLanguages,
			})
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * My Phone Info
	 * ==============
	 * API Endpoint: /web/my/phoneinfo
	 *
	 * Selects the currently logged in User's Phone Information
	 */
	userPhoneInfo(_userId: number): Observable<UserPhoneInfo[]> {
		return this.backendService
			.post(PHONE_INFO_URL, PHONE_INFO_RESPONSE, {})
			.pipe(
				map((response: UserPhoneInfoResponse) =>
					response.phone.map((json) => UserPhoneInfo.fromJSON(json))
				)
			);
	}

	/**
	 * My Passports
	 * =============
	 * API Endpoint: /web/my/passports
	 *
	 * Selects the currently logged in User's Passports
	 */
	userPassports(_userId: number): Observable<UserPassport[]> {
		return this.backendService
			.post(PASSPORTS_URL, PASSPORTS_RESPONSE, {})
			.pipe(
				map((response: UserPassportResponse) =>
					response.passports.map((json) =>
						UserPassport.fromJSON(json)
					)
				)
			);
	}
}
